import { useEffect, useState } from 'react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  ClassicProtection,
  ComplianceConventions,
  ComplianceFacts,
  DEFAULT_CONVENTIONS,
  RulesetRule,
  mergeProtection,
} from '../rules';

/** Same annotation the github-actions and github-insights plugins read. */
export const PROJECT_SLUG_ANNOTATION = 'github.com/project-slug';

/**
 * GitHub accepts CODEOWNERS in exactly these three locations. Checking only the
 * repo root reports a false negative for every repo that puts it in .github/,
 * which is the more common convention.
 */
const CODEOWNERS_PATHS = [
  'CODEOWNERS',
  '.github/CODEOWNERS',
  'docs/CODEOWNERS',
];

/**
 * Collects the facts the compliance rules are evaluated against.
 *
 * Called as the VIEWER, via scmAuth, rather than through the backend's GitHub
 * App. That is deliberate and it has a consequence worth knowing: somebody
 * without access to a repository sees the card fail to load rather than seeing
 * results they should not have. Using the App's installation token would show
 * everyone everything, which is a different product decision than this card is
 * entitled to make.
 */
export function useComplianceFacts(entity: Entity) {
  const configApi = useApi(configApiRef);
  const scmAuth = useApi(scmAuthApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [facts, setFacts] = useState<ComplianceFacts | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [loading, setLoading] = useState(true);

  const slug = entity.metadata.annotations?.[PROJECT_SLUG_ANNOTATION];
  const owner = entity.spec?.owner as string | undefined;
  const directSystem = entity.spec?.system as string | undefined;
  const serviceGroup = entity.spec?.serviceGroup as string | undefined;
  const namespace = entity.metadata.namespace ?? 'default';

  const conventions: ComplianceConventions = {
    ...DEFAULT_CONVENTIONS,
    ...(configApi
      .getOptionalConfig('githubCompliance.conventions')
      ?.get<Partial<ComplianceConventions>>() ?? {}),
  };

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(undefined);

        // Components inherit their system through ServiceGroup. Repository
        // topics can describe several services, so they cannot identify ours.
        const group =
          !directSystem && serviceGroup
            ? await catalogApi.getEntityByRef(
                parseEntityRef(serviceGroup, {
                  defaultKind: 'ServiceGroup',
                  defaultNamespace: namespace,
                }),
              )
            : undefined;
        const systemRef =
          directSystem ?? (group?.spec?.system as string | undefined);
        const system = systemRef
          ? parseEntityRef(systemRef, {
              defaultKind: 'System',
              defaultNamespace: namespace,
            }).name
          : undefined;

        const repoUrl = `https://github.com/${slug}`;
        const { token } = await scmAuth.getCredentials({
          url: repoUrl,
          additionalScope: { customScopes: { github: ['repo'] } },
        });

        const headers = {
          Authorization: `Bearer ${token}`,
          Accept: 'application/vnd.github+json',
          'X-GitHub-Api-Version': '2022-11-28',
        };
        const api = `https://api.github.com/repos/${slug}`;

        const repoRes = await fetch(api, { headers });
        if (!repoRes.ok) {
          throw new Error(
            repoRes.status === 404
              ? `Repository ${slug} not found, or your GitHub account cannot see it.`
              : `GitHub returned ${repoRes.status} for ${slug}.`,
          );
        }
        const repo = await repoRes.json();
        const defaultBranch: string = repo.default_branch;

        // GitHub has TWO protection systems and they cannot see each other:
        // classic branch protection, and the newer rulesets. Both are read,
        // then merged by mergeProtection.
        //
        // Reading only the classic endpoint is a FALSE NEGATIVE machine. A repo
        // protected purely by rulesets answers it with 404 "Branch not
        // protected" - WTS-Paradigm/port-webhooks does exactly that while
        // enforcing non_fast_forward and a one-approval pull_request rule - so
        // every protection check reported failing on a properly protected repo.
        //
        // The rules endpoint is also the better of the two: it returns the
        // rules actually in force, including ones inherited from ORGANISATION
        // rulesets, which the classic API cannot represent at all.
        const [protRes, rulesRes] = await Promise.all([
          fetch(
            `${api}/branches/${encodeURIComponent(defaultBranch)}/protection`,
            {
              headers,
            },
          ),
          fetch(`${api}/rules/branches/${encodeURIComponent(defaultBranch)}`, {
            headers,
          }),
        ]);

        // 404 from either is not an error, it is the answer: that system does
        // not protect this branch. Only when BOTH say nothing is the branch
        // genuinely unprotected.
        //
        // 403 on the CLASSIC endpoint is tolerated rather than fatal. It means
        // the viewer lacks admin, which used to make the whole card fail; the
        // rules endpoint needs only read access, so we can still answer every
        // check from it alone. Failing here would hide protection that we can
        // in fact see.
        let classic: ClassicProtection | undefined;
        if (protRes.ok) {
          const p = await protRes.json();
          classic = {
            requiredApprovingReviewCount:
              p.required_pull_request_reviews
                ?.required_approving_review_count ?? 0,
            requireCodeOwnerReviews: Boolean(
              p.required_pull_request_reviews?.require_code_owner_reviews,
            ),
            // GitHub omits these objects entirely when the setting is off, so an
            // absent key means false rather than unknown.
            allowForcePushes: Boolean(p.allow_force_pushes?.enabled),
            requireConversationResolution: Boolean(
              p.required_conversation_resolution?.enabled,
            ),
            requireSignedCommits: Boolean(p.required_signatures?.enabled),
            requireLinearHistory: Boolean(p.required_linear_history?.enabled),
          };
        } else if (protRes.status !== 404 && protRes.status !== 403) {
          throw new Error(
            `GitHub returned ${protRes.status} reading branch protection.`,
          );
        }

        let rules: RulesetRule[] = [];
        if (rulesRes.ok) {
          rules = (await rulesRes.json()) as RulesetRule[];
        } else if (rulesRes.status !== 404) {
          throw new Error(
            `GitHub returned ${rulesRes.status} reading branch rules.`,
          );
        }

        // Any one of the three locations counts. Fetched in parallel and with
        // HEAD, since only existence matters - the contents are irrelevant and
        // some CODEOWNERS files are large.
        const codeownersResults = await Promise.all(
          CODEOWNERS_PATHS.map(path =>
            fetch(`${api}/contents/${path}`, { method: 'HEAD', headers })
              .then(r => r.ok)
              .catch(() => false),
          ),
        );

        if (cancelled) return;

        setFacts({
          // The org is stripped: every naming rule is about the repo name alone.
          name: slug.split('/').pop()!,
          defaultBranch,
          topics: (repo.topics ?? []) as string[],
          hasCodeowners: codeownersResults.some(Boolean),
          protection: mergeProtection(classic, rules),
          owner,
          system,
        });
      } catch (e) {
        if (!cancelled) setError(e as Error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, owner, directSystem, serviceGroup, namespace, scmAuth, catalogApi]);

  return { facts, conventions, loading, error, slug };
}
